import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Badge,
  OutlinedInput,
  Box,
  Chip,
  InputLabel,
  Grid,
  Typography,
  TextField,
  Button,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import './Filters.scss';
import { FilterProps } from '@consolidocs-ui/types';
import { useSearchParams } from 'react-router';
import { DOCUMENT_TYPES, FILTER_KEYS } from './constants';
import { useTranslation } from 'react-i18next';

type Props = {
  onChange: (filters: FilterProps) => void;
  useGetCategoriesQuery: any; //CategoryPropsType
};

const Filters: FC<Props> = ({ onChange, useGetCategoriesQuery }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { data } = useGetCategoriesQuery();

  const filtersFromQuery = useMemo((): FilterProps => {
    const contractTypeIds = searchParams.get('contract_type_ids');

    return {
      contract_type_ids: contractTypeIds ? contractTypeIds.split(',') : [],
      search: searchParams.get('search') || '',
      collection_date_start: searchParams.get('collection_date_start') || '',
      collection_date_end: searchParams.get('collection_date_end') || '',
      contract_date_start: searchParams.get('contract_date_start') || '',
      contract_date_end: searchParams.get('contract_date_end') || '',
      document_type: searchParams.get('document_type') || '',
      amendment_date_start: searchParams.get('amendment_date_start') || '',
      amendment_date_end: searchParams.get('amendment_date_end') || '',
    };
  }, [searchParams]);

  type CategoryOption = { id: string; name: string };
  const categoryOptions: CategoryOption[] = data?.data ?? [];

  const updateQueryParams = useCallback(
    (newFilters: Partial<FilterProps>) => {
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);

        Object.entries(newFilters).forEach(([key, value]) => {
          if (
            value === undefined ||
            value === '' ||
            (Array.isArray(value) && value.length === 0)
          ) {
            newParams.delete(key);
          } else if (Array.isArray(value)) {
            newParams.set(key, value.join(','));
          } else {
            newParams.set(key, String(value));
          }
        });

        return newParams;
      });
    },
    [setSearchParams]
  );

  const handleFieldChange = useCallback(
    <K extends keyof FilterProps>(key: K, value: FilterProps[K]) => {
      updateQueryParams({ [key]: value });
    },
    [updateQueryParams]
  );

  const handleReset = () => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      FILTER_KEYS.forEach((key) => newParams.delete(key));
      return newParams;
    });
  };

  const handleChipDelete = (valueToRemove: string) => {
    const newIds =
      filtersFromQuery.contract_type_ids?.filter(
        (id) => id !== valueToRemove
      ) ?? [];
    handleFieldChange('contract_type_ids', newIds);
  };

  useEffect(() => {
    const handler = setTimeout(() => onChange(filtersFromQuery), 300);
    return () => clearTimeout(handler);
  }, [filtersFromQuery, onChange]);

  const activeFilterCount = useMemo(() => {
    return Object.entries(filtersFromQuery).filter(([_, value]) => {
      return Array.isArray(value) ? value.length > 0 : value !== '';
    }).length;
  }, [filtersFromQuery]);

  return (
    <>
      <Badge
        badgeContent={activeFilterCount}
        color="primary"
        invisible={!activeFilterCount}
        sx={{
          height: '34px',
          margin: 'auto',
          '& .MuiBadge-badge': {
            backgroundColor: '#6a1b9a',
            color: '#fff',
          },
        }}
      >
        <IconButton
          color="primary"
          sx={{ height: '34px' }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <FilterListIcon fontSize="small" sx={{ color: '#8e8e93' }} />
        </IconButton>
      </Badge>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 250,
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
          },
        }}
      >
        <div className="filter-menu-header">
          <Typography variant="h6" gutterBottom>
            {t('Filters and Search')}
          </Typography>
          <Button
            variant="text"
            onClick={handleReset}
            disabled={!activeFilterCount}
          >
            {t('Reset')}
          </Button>
        </div>

        <Grid container flexDirection={'column'} spacing={3} marginTop="18px">
          <Grid>
            <Box display="flex" gap={1}>
              <TextField
                size="small"
                label={t('Search')}
                fullWidth
                placeholder={t('Search')}
                value={filtersFromQuery.search}
                onChange={(e) => handleFieldChange('search', e.target.value)}
              />
            </Box>
          </Grid>

          <Grid>
            <FormControl fullWidth>
              <InputLabel shrink>{t('Contract Type')}</InputLabel>
              <Select
                multiple
                value={filtersFromQuery.contract_type_ids}
                onChange={(e) =>
                  handleFieldChange(
                    'contract_type_ids',
                    e.target.value as string[]
                  )
                }
                input={
                  <OutlinedInput
                    notched
                    label={t('Contract Type')}
                    sx={{ padding: '9px' }}
                  />
                }
                renderValue={(selected: string[]) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value: string) => (
                      <Chip
                        key={value}
                        label={
                          categoryOptions.find((option) => option.id === value)
                            ?.name ?? 'not-found'
                        }
                        size="small"
                        onDelete={(e) => {
                          e.stopPropagation();
                          handleChipDelete(value);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    ))}
                  </Box>
                )}
              >
                {categoryOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={1}
            >
              <TextField
                size="small"
                type="date"
                label={t('Contract Start')}
                fullWidth
                value={filtersFromQuery.contract_date_start ?? ''}
                onChange={(e) =>
                  handleFieldChange('contract_date_start', e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label={t('Contract End')}
                fullWidth
                value={filtersFromQuery.contract_date_end ?? ''}
                onChange={(e) =>
                  handleFieldChange('contract_date_end', e.target.value)
                }
                inputProps={{
                  min: filtersFromQuery.contract_date_start ?? undefined,
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Grid>

          <Grid>
            <FormControl fullWidth size="small">
              <InputLabel id="doc-type-label">{t('Document type')}</InputLabel>
              <Select
                value={filtersFromQuery.document_type}
                onChange={(e) =>
                  handleFieldChange('document_type', e.target.value)
                }
                input={
                  <OutlinedInput
                    sx={{ padding: '9px' }}
                    label={t('Document type')}
                  />
                }
                labelId="doc-type-label"
              >
                <MenuItem value="">{t('None')}</MenuItem>
                {DOCUMENT_TYPES.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {t(label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={1}
            >
              <TextField
                size="small"
                type="date"
                label={t('Amendment Start')}
                fullWidth
                value={filtersFromQuery.amendment_date_start}
                onChange={(e) =>
                  handleFieldChange('amendment_date_start', e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label={t('Amendment End')}
                fullWidth
                value={filtersFromQuery.amendment_date_end}
                onChange={(e) =>
                  handleFieldChange('amendment_date_end', e.target.value)
                }
                inputProps={{
                  min: filtersFromQuery.amendment_date_start || undefined,
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Grid>

          <Grid>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={1}
            >
              <TextField
                size="small"
                type="date"
                label={t('Collection Start')}
                fullWidth
                value={filtersFromQuery.collection_date_start}
                onChange={(e) =>
                  handleFieldChange('collection_date_start', e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label={t('Collection End')}
                fullWidth
                value={filtersFromQuery.collection_date_end}
                onChange={(e) =>
                  handleFieldChange('collection_date_end', e.target.value)
                }
                inputProps={{
                  min: filtersFromQuery.collection_date_start || undefined,
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Grid>
        </Grid>
      </Menu>
    </>
  );
};

export default Filters;
